import { Controller } from "@hotwired/stimulus"

// UI-01 dashboard auto-refresh — polls a `<turbo-frame>` every `intervalValue` ms.
// Pauses while the tab is hidden (`document.visibilityState === "hidden"`) to avoid
// hammering the server with refreshes nobody is looking at. Triggers
// `frame.reload()` which re-runs the controller action server-side and re-renders
// the partial — Stimulus controllers inside the frame auto-reconnect.
//
// Auto-registered via the `controllers/index.js` esbuild glob (do NOT edit index.js).
//
// Values:
//   interval — polling interval in ms (e.g. 300000 = 5 min)
//   frame    — target `<turbo-frame>` id to reload
//
// Targets:
//   countdown (optional) — DaisyUI radial-progress 링 버튼. --value에는 남은
//   비율(%)을, textContent에는 남은 초를 1초 틱으로 기록해 링이 30→0초로
//   줄어드는 시계처럼 보인다(클릭 = refreshNow, 별도 새로고침 버튼 없음).
//   순수 클라이언트 상태(다음 폴링까지의 시간)라 WR-05가 제거했던 클라이언트
//   측 "방금 전" 표기와 달리 서버 렌더 시각과 충돌하지 않는다. 탭 숨김 시
//   폴링과 함께 멈추고, 복귀 시 만기부터 다시 센다.
export default class extends Controller {
  static values = { interval: Number, frame: String }
  static targets = ["countdown"]

  connect() {
    this.schedule()
    this.boundVisibility = this.handleVisibility.bind(this)
    document.addEventListener("visibilitychange", this.boundVisibility)
  }

  disconnect() {
    clearTimeout(this.timer)
    clearInterval(this.ticker)
    document.removeEventListener("visibilitychange", this.boundVisibility)
  }

  handleVisibility() {
    if (document.visibilityState === "hidden") {
      clearTimeout(this.timer)
      clearInterval(this.ticker)
    } else {
      this.schedule()
    }
  }

  schedule() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.refreshNow(), this.intervalValue)
    this.startCountdown()
  }

  startCountdown() {
    if (!this.hasCountdownTarget) return

    this.deadline = Date.now() + this.intervalValue
    clearInterval(this.ticker)
    this.renderCountdown()
    this.ticker = setInterval(() => this.renderCountdown(), 1000)
  }

  renderCountdown() {
    const totalSeconds = this.intervalValue / 1000
    const remaining = Math.max(0, Math.ceil((this.deadline - Date.now()) / 1000))
    const percent = Math.round((remaining / totalSeconds) * 100)
    const el = this.countdownTarget
    el.style.setProperty("--value", percent)
    el.textContent = remaining
    el.setAttribute("aria-label", `다음 갱신까지 ${remaining}초 — 클릭하면 지금 갱신`)
  }

  // UAT finding (2026-05-12): `<turbo-frame id="device-status">` has no `src`
  // attribute (server-renders inline), so `frame.reload()` is a no-op — Turbo
  // has no URL to re-fetch from. Switched to `Turbo.visit()` which navigates to
  // the same URL via Turbo Drive: the frame re-renders, the header (with the
  // KST timestamp) refreshes, and Stimulus controllers inside the frame
  // re-connect cleanly. Falls back to `location.reload()` if Turbo is absent
  // (defensive — should never trigger in this app since turbo-rails is loaded).
  refreshNow() {
    if (window.Turbo && typeof window.Turbo.visit === "function") {
      window.Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
    this.schedule()
  }
}
